import React from 'react';
import { IMAGES } from '../constants/images';
type Props = {
  height?: number;
  className?: string;
  /**
   * 'light' (default) renders the mark for placement on DARK surfaces:
   * the source black-on-white logo is inverted to cream/white lettering and
   * its background is dropped out via screen blending.
   * 'dark' renders dark lettering for placement on LIGHT surfaces (white
   * background dropped via multiply, no inversion).
   */
  tone?: 'light' | 'dark';
  /** Accepted for backwards compatibility; unused by the image mark. */
  color?: string;
  strokeWidth?: number;
};
/**
 * The official TARTA wordmark, rendered from the brand logo PNG
 * (black lettering on a clean white field). We crop tightly to the
 * lettering within the square source and drop the white background out
 * with a blend mode so the mark sits cleanly on its surface.
 *
 * Source: 1024×1024 PNG; the lettering occupies a centered horizontal
 * strip. The crop window below isolates just the wordmark.
 */
// Fraction of the source occupied by the wordmark lettering.
const CROP = {
  // horizontal extent of the lettering within the 1024 canvas
  left: 0.27,
  right: 0.73,
  // vertical extent
  top: 0.43,
  bottom: 0.52
};
const CROP_W = CROP.right - CROP.left; // 0.46
const CROP_H = CROP.bottom - CROP.top; // 0.09
const ASPECT = CROP_W / CROP_H; // ~5.1 : 1
export function TartaWordmark({
  height = 28,
  className = '',
  tone = 'light'
}: Props) {
  const width = height * ASPECT;
  // background-size: scale the full image so the crop window maps to the box.
  const bgW = width / CROP_W;
  const bgH = height / CROP_H;
  // background-position: shift so the crop's top-left aligns to the box.
  const posX = -CROP.left * bgW;
  const posY = -CROP.top * bgH;
  const isLight = tone === 'light';
  return <span role="img" aria-label="TARTA" className={className} style={{
    display: 'inline-block',
    width,
    height,
    backgroundImage: `url(${IMAGES.logoWordmark})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: `${bgW}px ${bgH}px`,
    backgroundPosition: `${posX}px ${posY}px`,
    // Drop the white field and (for dark surfaces) flip black -> cream.
    filter: isLight ? 'invert(1)' : 'none',
    mixBlendMode: isLight ? 'screen' : 'multiply'
  }} data-id="element-361" data-mp="11nvl8z7ror72" />;
}